import { Component, Input, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  ShoppingCartIcon,
  StarIcon,
} from 'lucide-angular';
import type { Producto } from '../../../types/product.types';
import { CartService } from '../../../services/cart.service';
import { NotificationService } from '../../../services/notification.service';
import { VehicleCompatibilityComponent } from './vehicle-compatibility.component';
import { DeliveryTypeByStore } from '../delivery-type-by-store/delivery-type-by-store';
import { ProductPrice } from '../price/price';
import { WarrantyGuard } from '../warranty-guard/warranty-guard';
import { CoreDeposit } from '../core-deposit/core-deposit';

@Component({
  selector: 'allas-product-card',

  imports: [
    CommonModule,
    LucideAngularModule,
    VehicleCompatibilityComponent,
    DeliveryTypeByStore,
    ProductPrice,
    WarrantyGuard,
    CoreDeposit,
  ],
  template: `
    <article
      class="bg-white border border-gray-200 rounded-lg p-2 hover:border-gray-300 transition-all duration-200 flex flex-col h-full fs-condensed"
    >
      <a
        [href]="'/productos/' + product.slug"
        class="flex flex-col h-full"
        [attr.aria-label]="'Ver detalles de' + product.nombre_web"
      >
        <!-- Product Image Section -->
        <ng-container [ngTemplateOutlet]="productImage"></ng-container>

        <!-- Product Information -->
        <section class="flex flex-col flex-1">
          <ng-container [ngTemplateOutlet]="productInfo"></ng-container>

          <!-- Pricing and Actions -->
          <div class="mt-4">
            <ng-container [ngTemplateOutlet]="pricingSection"></ng-container>
          </div>
        </section>

        <!-- Actions Container -->
        <section class="flex flex-col">
          <ng-container [ngTemplateOutlet]="actionButtons"></ng-container>
        </section>
      </a>
    </article>

    <!-- Product Image Template -->
    <ng-template #productImage>
      <div class="aspect-square bg-gray-50 rounded-lg mb-4 overflow-hidden">
        <img
          [src]="product.imagenes.thumb[0] || '/placeholder.svg'"
          [alt]="
            product.descripcion || 'Imagen del producto ' + product.nombre_web
          "
          class="w-full h-full object-contain"
          decoding="async"
          fetchpriority="high"
          width="200"
          height="200"
        />
      </div>
    </ng-template>

    <!-- Product Information Template -->
    <ng-template #productInfo>
      <div class="space-y-3">
        <!-- Title -->
        <h2 class="font-semibold text-gray-900 text-sm leading-tight">
          {{ product.nombre_web }}
        </h2>

        <!-- Part Numbers -->
        <p class="text-[10px] text-gray-500">
          Parte #{{ product.part_number }}
          <span class="text-gray-300 text-[8px]" aria-hidden="true">|</span>
          Código #{{ product.sku.trim() }}
        </p>

        <!-- Warranty -->
        <ng-container [ngTemplateOutlet]="warrantySection"></ng-container>

        <!-- Rating -->
        <!-- <ng-container [ngTemplateOutlet]="ratingSection"></ng-container> -->

        <!-- Vehicle Compatibility -->
        <allas-vehicle-compatibility
          [product]="product"
          [status]="compatibleWithVehicle ? 'compatible' : 'not-selected'"
          [defaultVehicle]="defaultVehicleAlias"
        ></allas-vehicle-compatibility>

        <!-- Product Attributes -->
        <ng-container [ngTemplateOutlet]="attributesSection"></ng-container>
      </div>
    </ng-template>

    <!-- Warranty Section Template -->
    <ng-template #warrantySection>
      @if (warrantyYears() > 0) {
      <allas-warranty-guard
        [months]="warrantyYears() * 12"
        styleClass="mt-2"
      ></allas-warranty-guard>
      }
    </ng-template>

    <!-- Rating Section Template -->
    <ng-template #ratingSection>
      <div
        class="flex items-center gap-1"
        role="img"
        [attr.aria-label]="
          'Calificación de' + product.rating + ' estrellas de 5'
        "
      >
        @for (item of [1,2,3,4,5]; track $index) {
        <lucide-icon
          [name]="starIcon"
          class="w-4 h-4"
          [ngClass]="{
            'text-gray-300': item > product.rating,
            'text-yellow-500': item <= product.rating
          }"
          [attr.aria-hidden]="true"
        />
        }
        <span class="text-xs">
          {{ product.rating }}
        </span>
        <span class="text-xs underline"> ({{ product.votes }}) </span>
      </div>
    </ng-template>

    <!-- Attributes Section Template -->
    <ng-template #attributesSection>
      <div class="flex flex-wrap gap-2">
        @for (attribute of attributesToDisplay; track $index) {
        <span
          class="rounded-lg px-3 py-1 text-[10px] transition-colors duration-200 bg-gray-100 text-gray-800"
        >
          <strong>{{ attribute.label }}:</strong> {{ attribute.value }}
        </span>
        }
      </div>
    </ng-template>

    <!-- Pricing Section Template -->
    <ng-template #pricingSection>
      <!-- Si tiene precio, mostrar -->
      <!-- Price -->
      <allas-product-price [price]="product.precio"></allas-product-price>

      @if (product.precio > 0) {
      <!-- Core Deposit -->
      @if (product.subcategoria === 'bateria') {
      <allas-core-deposit [product]="product"></allas-core-deposit>
      }

      <!-- Delivery Options -->
      <allas-delivery-type-by-store
        [product]="product"
      ></allas-delivery-type-by-store>
      }
    </ng-template>

    <!-- Action Buttons Template -->
    <ng-template #actionButtons>
      <!-- Add to Cart Button -->
      <button
        (click)="addToCart()"
        class="w-full bg-appPrimary-500 hover:bg-appPrimary-600 text-white py-2 px-4 rounded font-semibold text-sm transition-colors flex items-center justify-center space-x-2 mt-2"
      >
        <lucide-icon
          [name]="shoppingCartIcon"
          class="w-4 h-4"
          aria-hidden="true"
        ></lucide-icon>
        <span>Agregar al carrito</span>
      </button>

      <!-- Product Notes -->
      <!-- ! Esta parte no irá aquí, se manejará en el componente de compatibilidad -->
      <ng-container [ngTemplateOutlet]="productNotes"></ng-container>
    </ng-template>

    <!-- Product Notes Template -->
    <ng-template #productNotes>
      @if (product.descripcion) {
      <div class="text-xs text-gray-500 mt-2">
        <!-- <span class="font-bold">Nota:</span> -->
        <span class="text-gray-500">
          {{ product.descripcion }}
        </span>
      </div>
      }
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProductCard {
  @Input() product!: Producto;
  @Input() compatibleWithVehicle = false;
  @Input() defaultVehicleAlias = '';

  private cartService = inject(CartService);
  private notificationService = inject(NotificationService);
  readonly shoppingCartIcon = ShoppingCartIcon;
  readonly starIcon = StarIcon;
  readonly warrantyYears = computed(() => {
    return this.getWarrantyYears(this.product.marca);
  });

  addToCart(): void {
    this.cartService.addItemToCart({
      producto: this.product,
      cantidad: 1,
    });
    this.notificationService.showSuccess(
      `${this.product.nombre_web} agregado al carrito`,
      'Producto disponible para cotización'
    );
  }

  get attributesToDisplay() {
    return this.product.atributos.filter(
      (attribute) => attribute.filterable && !attribute.is_from_api_catalogo
    );
  }

  getIntFormattedPrice(): string {
    // Sin redondear a nada, solo quitar los decimales, agregar las comas para separar los miles
    const intPrice = Number(this.product.precio.toString().split('.')[0]);
    return intPrice.toLocaleString('es-CO').replace(/\./g, ',');
  }

  getOnlyDecimalPrice(): string {
    const decimal = this.product.precio.toString().split('.')[1] ?? '00';
    return decimal.length === 1 ? `0${decimal}` : decimal;
  }

  getWarrantyYears(brand: string): number {
    switch (brand) {
      case 'TAB POLAR':
        return 3;
      case 'TAB 5G':
        return 4;
      case 'TAB AGM STOP & GO':
        return 5;
      default:
        return 0;
    }
  }
}
